<script lang="ts">
    import type { TrackListColumn } from './types';
    import { intersectionObserver } from '@amp/web-app-components/src/actions/intersection-observer';

    export let columns: TrackListColumn[];
    export let isVisible: boolean = true;

    let headerIsSticking = false;

    /**
     * Make the header sticky when it gets out of the main content area.
     * Note: never stick the header itself with styling, but only children. Otherwise it will remain sticky.
     */
    function setStickyHeader(isIntersectingViewport: boolean) {
        headerIsSticking = !isIntersectingViewport;
    }
</script>

<!-- Always needs header columns for AX, even when the Jet data doesn't give us -->
<div
    data-testid="tracklist-column-header"
    class="songs-list__header"
    class:songs-list__header--is-visible={isVisible}
    class:is-sticking={headerIsSticking}
    role="row"
    use:intersectionObserver={{
        callback: setStickyHeader,
        threshold: 1,
        root: document.querySelector('[data-main-content]'),
    }}
>
    {#each columns as col}
        <div
            class="songs-list__col songs-list__col--{col.key} songs-list__header-col songs-list__header-col--{col.key}"
            role="columnheader"
            data-testid="tracklist-column-header-{col.key}"
        >
            <div
                class="songs-list__header-col-label songs-list__header-col-label--{col.key}"
            >
                {col.headerText}
            </div>
        </div>
    {/each}
</div>

<style lang="scss">
    // TODO: rdar://89617186 (JMOTW: UXD: Tracklist: investigate how we can have shared styling between different files)
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use '@amp/web-shared-styles/sasskit-stylekit/core' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use './styles/shared.scss' as *;

    $web-chrome-box-shadow-light: 0 1px 0 rgba(0, 0, 0, 0.05),
        0 1px 3px var(--stickyHeaderDropShadow2),
        inset 0 1px 1px var(--stickyHeaderInnerShadow);
    $web-chrome-box-shadow-dark: 0 1px 0 rgba(0, 0, 0, 0.05),
        0 1px 3px var(--stickyHeaderDropShadow2),
        inset 0 -0.5px 0 var(--stickyHeaderInnerShadow);

    @mixin visually-hide-header {
        @include visuallyhidden;
        // `table-layout: fixed` fails with `position: absolute;` in `visuallyhidden` mixin
        // Since first (header) row needs to define column widths
        position: static; // stylelint-disable-line order/order

        .songs-list__header-col,
        .songs-list__header-col-label {
            // Collapse header contents
            height: 0;
            padding: 0;
            line-height: 0;
            font-size: 0;

            &::before {
                // Hide header dividers
                content: none;
            }
        }
    }

    .songs-list__header {
        display: table-row;
        position: relative;
        font: var(--callout-emphasized);
        color: var(--systemSecondary);

        @media (--range-xsmall-down) {
            @include visually-hide-header;
        }

        &:not(.songs-list__header--is-visible) {
            @include visually-hide-header;
        }

        &:hover,
        &:focus {
            background-color: transparent;
        }
    }

    .songs-list__header-col {
        padding-bottom: $songs-list-header-col-bottom-spacer;
        height: $songs-list-header-height;
        border-radius: 0;
        font: var(--callout-emphasized);
        position: relative;
        z-index: var(--z-default);

        // Playlist sticky header
        @media (--small) {
            :global(.songs-list__header--is-visible) &,
            :global(.room) & {
                position: sticky;
                top: 0;
                z-index: calc(var(--z-default) + 1);
            }
        }
    }

    //
    // Songs List Header Column Widths
    //
    // It's more efficient for the browser to calculate all column widths from the header row
    .songs-list__header-col--index {
        width: 30px;
    }

    .songs-list__header-col--prefix {
        width: var(--bodyGutter);
    }

    .songs-list__header-col--favorite-or-popular {
        width: 0;

        // Playlist sticky header (full-width) opaque bar
        @media (--small) {
            .songs-list__header-col-label {
                // Sticky header styles, only visible when sticking
                .songs-list__header--is-visible.is-sticking & {
                    width: 100vw;
                    height: $songs-list-header-height;
                    position: absolute;
                    top: 0;
                    box-shadow: $web-chrome-box-shadow-light;
                    background-color: var(
                        --pageBG
                    ); // Match to web background-color
                    inset-inline-start: calc(
                        -#{$songs-list-start-padding} + var(--bodyGutter)
                    );
                    z-index: var(--z-default);

                    @media (prefers-color-scheme: dark) {
                        box-shadow: $web-chrome-box-shadow-dark;
                    }

                    @media (--xlarge) {
                        // Extend bar, beyond max-content width, to the edges of main container
                        // Subtract max-content width, sidebar and gutters, from the full window width, to align bar with sidebar edge
                        inset-inline-start: calc(
                            -1 * (
                                    100vw - #{viewport-content-for(xlarge)} - var(
                                            --web-navigation-width
                                        ) + var(--bodyGutter) * 2
                                ) / 2
                        );
                    }
                }

                // Sticky header, on Chrome, causes major page layout issue with IntersectionObserver and scrolling with third-party mice
                // Positioning the opaque bar's container on Chrome, in RTL, before it sticks can solve this issue.
                @include target-chrome {
                    // Using `:global(html[dir=rtl])` since Svelte fails to compile styles when `rtl` mixin is used inside `target-chrome`
                    :global(html[dir='rtl']) & {
                        width: 100vw;
                        height: $songs-list-header-height;
                        position: absolute;
                        top: 0;
                        right: calc(
                            -#{$songs-list-start-padding} + var(--bodyGutter)
                        );
                        z-index: var(--z-default);

                        @media (--xlarge) {
                            // Extend bar, beyond max-content width, to the edges of main container
                            // Subtract max-content width, sidebar and gutters, from the full window width, to align bar with sidebar edge
                            right: calc(
                                -1 * (
                                        100vw - #{viewport-content-for(xlarge)} -
                                            var(--web-navigation-width) + var(
                                                --bodyGutter
                                            ) * 2
                                    ) / 2
                            );
                        }
                    }
                }
            }
        }
    }

    .songs-list__header-col--song {
        @media (--small) {
            width: 50%;
        }

        @media (--medium) {
            width: 42%;

            @include feature-detect(browse-top-charts) {
                width: 46%; // Compensate for rankings
            }
        }
    }

    .songs-list__header-col--secondary,
    .songs-list__header-col--tertiary,
    .songs-list__header-col--quaternary {
        .songs-list__header:not(.songs-list__header--is-visible) & {
            width: 20%;
            margin-inline-start: 10px;
        }

        @media (--medium) {
            width: 29%;

            @include feature-detect(browse-top-charts) {
                width: 27%; // Compensate for rankings
            }
        }
    }

    // stylelint-disable-next-line no-duplicate-selectors
    .songs-list__header:not(.songs-list__header--is-visible) {
        .songs-list__header-col--song {
            width: auto;
        }
    }

    .songs-list__header-col-label {
        @include line-clamp;
    }

    .songs-list__header-col-label--time {
        // Add padding to increase height of element, so that divider is tall enough for hidden overflow
        padding-top: 3px;
        padding-bottom: 3px;
        display: inline-block;
        position: relative;
        z-index: var(--z-default);
        padding-inline-start: $songs-list-header-start-padding;
        padding-inline-end: $action-button-size +
            $songs-list-row-context-menu-spacer;
    }

    .songs-list__header-col:nth-child(2) {
        padding-inline-start: $songs-list-start-padding;
    }

    // Vertical divider
    .songs-list__header-col::before,
    .songs-list__header-col-label--time::before {
        display: inline-block;
        content: '';
        height: 16px;
        width: 1px;
        position: absolute;
        top: calc(50% - ($songs-list-header-col-bottom-spacer / 2));
        border-radius: 0.5px;
        background: var(--systemQuaternary);
        transform: translateY(-50%);
        inset-inline-start: -$songs-list-header-start-padding;
    }

    .songs-list__header-col-label--time::before {
        top: $songs-list-header-col-bottom-spacer * 2;
        inset-inline-start: 0;
    }

    // Hide vertical divider
    .songs-list__header-col--time,
    .songs-list__header-col:first-child,
    .songs-list__header-col:nth-child(2) {
        &::before {
            content: none;
        }
    }
</style>
